import { getStatusColor } from "utils/helper";
import { PriorityTypesMap } from "./inventoryStatus";

// Single source of colour for the Inventory dashboard. Where the app already
// assigns a colour (status badges via getStatusColor, HomeModule stat tiles)
// we reuse it, so a status/metric looks the same here as everywhere else.

// getStatusColor returns either "indigo" or "brand.3"; normalise to "color.shade".
const withShade = (color, shade = 6) =>
  color.includes(".") ? color : `${color}.${shade}`;

// Mantine colour ("red.6") -> CSS variable usable in SVG/inline styles.
export const mantineColor = (color) =>
  `var(--mantine-color-${color.replace(".", "-")})`;

// Sample statuses / production stages. Used by Sample Urgency (chips, points,
// matrix) and Items per Stage.
export const STATUS_COLORS = {
  // Not yet in the workflow - neutral.
  "Not Yet Actioned": "gray.5",
  // App: ACQUIRED badge.
  Acquired: withShade(getStatusColor("ACQUIRED")),
  // App: SCHEDULED badge.
  Scheduled: withShade(getStatusColor("SCHEDULED")),
  // No app colour for captured; cyan is unused elsewhere so it stays distinct.
  Captured: "cyan.6",
  // App: work-in-review statuses (REVIEW / INAPPROVAL / PENDING) are orange.
  "Post Production": withShade(getStatusColor("REVIEW")),
  // App: DISTRIBUTED badge + HomeModule "Distributed Styles" tile.
  Distributed: withShade(getStatusColor("DISTRIBUTED")),
  // App: COMPLETE badge - the sample's journey is finished.
  Returned: withShade(getStatusColor("COMPLETE")),
};

// Inventory Overview series; the stat tiles share these via their dots.
export const OVERVIEW_SERIES = [
  // Overall total uses the app's primary (brand) colour.
  { name: "Inventory", color: "brand.6" },
  // Photographs are the captured output, so they match Captured.
  { name: "Assets", color: STATUS_COLORS.Captured },
  // Grape rather than HomeModule's pink "Products Scanned In" tile, as pink
  // sits too close to the red Errant Scans series.
  { name: "Checked In", color: "grape.6" },
  // Scheduled sessions match the Scheduled status.
  { name: "Scheduled", color: STATUS_COLORS.Scheduled },
  // Retouching is post production work, so it matches that status.
  { name: "Retouching", color: STATUS_COLORS["Post Production"] },
  { name: "Distributed", color: STATUS_COLORS.Distributed },
  
  
  // Errors are red across the app (getStatusColor's fallback).
  { name: "Errant Scans", color: "red.6" },
];

export const ERRANT_COLOR = "red.6";

// Matches the bars in HomeModule's "Distributions by Department" chart: each
// data row sets color "blue.4", which Mantine's BarChart draws over the
// series colour. Categories and channels both use it.
export const CATEGORY_COLOR = "blue.4";
export const CHANNEL_COLOR = CATEGORY_COLOR;

// Urgency is a severity scale, so it uses a single red ramp rather than
// separate hues. Distinct hues would clash with the status colours drawn on
// top of these columns (orange post production, blue distributed, teal
// returned). Error (no/invalid OTB) is neutral, as it's a data problem.
// Defined on PriorityTypesMap so the list's priority badges match. Keyed by
// label, which matches the URGENCY_LEVELS names the dashboard looks up by.
export const URGENCY_COLORS = Object.fromEntries(
  Object.values(PriorityTypesMap).map((type) => [type.label, withShade(type.color)])
);

// Positive / negative change, matching teal = success across the app.
export const TREND_GOOD = "teal";
export const TREND_BAD = "red";
